import { useCallback, useRef } from 'preact/hooks';
import { Item as CurrentItem } from '#UNSAFE_Collection';
import { ListProps } from './List.types';

/**
 * This hook handles the currentItemOverride passing to PRIVATE_List layer.
 *
 * If the app provided new value for 'currentItemOverride', we pass that down to PRIVATE_List layer;
 * otherwise, we will pass down the value of internal override, which would be the value
 * updated by useListViewContextMenu hook
 *
 * @param currentItemOverride
 */
export const useListViewCurrentItemOverride = <K extends string | number, D>(
  currentItemOverride: ListProps<K, D>['currentItemOverride']
) => {
  // the override that will be passed down to PRIVATE_List layer
  const listOverrideRef = useRef<CurrentItem<K>>();

  // track the currentItemOverride attribute set by app
  const appOverrideRef = useRef<CurrentItem<K>>();
  if (appOverrideRef.current !== currentItemOverride) {
    appOverrideRef.current = currentItemOverride;
    listOverrideRef.current = currentItemOverride;
  }

  // track the internal override updated by useListViewContextMenu hook
  // we can just update ref here since the hook will trigger a state update for its context, which will trigger a rerender
  const updateCurrentItemOverride = useCallback((key: K) => {
    listOverrideRef.current = { rowKey: key };
  }, []);

  return {
    currentItemOverride: listOverrideRef.current,
    updateCurrentItemOverride
  };
};
